import { type ComponentProps, memo, useCallback } from 'react';

import { useTrackCreditMeterUpsellClicked } from '@air/analytics';
import { Caution as CautionIcon } from '@air/next-icons';
import { Button } from '@air/primitive-button';
import { useAirModal } from '@air/provider-modal';
import { cn, tailwindVariants } from '@air/utils-tailwind';

import { CreditUsageBreakdownCategory } from '~/components/CreditUsageMeter/CreditUsageBreakdownCategory';
import { CreditUsageMeterBar } from '~/components/CreditUsageMeter/CreditUsageMeterBar';
import { CreditUsageMeterResetDate } from '~/components/CreditUsageMeter/CreditUsageMeterResetDate';
import { type CreditUsageStats } from '~/components/CreditUsageMeter/types';
import { CREDIT_USAGE_CATEGORIES, getCreditUsageState, getPercentageUsed } from '~/components/CreditUsageMeter/utils';
import { PlansModal } from '~/components/Modals/PlansModal/PlansModal';
import { useCreditAnalyticsParams } from '~/hooks/useCreditAnalyticsParams';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useSubscriptionContext } from '~/providers/SubscriptionProvider';
import { canManageSubscription } from '~/utils/permissions/workspacePermissions';

const creditUsageBreakdownVariants = tailwindVariants({
  slots: {
    banner: 'flex items-center justify-center gap-1 px-3 py-2.5 rounded-t-8',
    bannerIcon: 'size-3',
    bannerText: 'text-12',
  },
  variants: {
    usageState: {
      default: {
        banner: '',
        bannerIcon: '',
        bannerText: '',
      },
      '50%': {
        banner: '',
        bannerIcon: '',
        bannerText: '',
      },
      '75%': {
        banner: 'bg-amber-4',
        bannerIcon: 'text-amber-11',
        bannerText: 'text-amber-12',
      },
      '90%': {
        banner: 'bg-flash-4',
        bannerIcon: 'text-flash-11',
        bannerText: 'text-flash-12',
      },
      depleted: {
        banner: 'bg-flash-4',
        bannerIcon: 'text-flash-11',
        bannerText: 'text-flash-12',
      },
    },
  },
});

export type CreditUsageBreakdownProps = Pick<ComponentProps<'div'>, 'className' | 'onMouseEnter' | 'onMouseLeave'> &
  Pick<CreditUsageStats, 'allocated' | 'used' | 'nextCycleStartAt'> & {
    includeMeterBar?: boolean;
  };

export const CreditUsageBreakdown = memo(
  ({ includeMeterBar, className, ...creditUsageStats }: CreditUsageBreakdownProps) => {
    const { allocated, used, nextCycleStartAt } = creditUsageStats;
    const usageState = getCreditUsageState({ used, allocated });

    const { data: permissions } = useCurrentWorkspacePermissionsContext();
    const canUpgradePlan = canManageSubscription(permissions);
    const { data: subscription } = useSubscriptionContext();
    const [showPlansModal] = useAirModal(PlansModal);
    const { trackCreditMeterUpsellClicked } = useTrackCreditMeterUpsellClicked();
    const creditAnalyticsParams = useCreditAnalyticsParams();

    const showPlansButton = canUpgradePlan && !subscription?.isEnterprise && !!allocated;

    const handleSeePlansClick = useCallback(() => {
      if (creditAnalyticsParams) {
        trackCreditMeterUpsellClicked(creditAnalyticsParams);
      }
      showPlansModal({ leadFormTrigger: 'credit_meter' });
    }, [creditAnalyticsParams, showPlansModal, trackCreditMeterUpsellClicked]);

    const percentageUsed = Math.round(getPercentageUsed(used.total, allocated ?? 0));

    return (
      <div className={cn('rounded-8 bg-gray-1', className)}>
        {usageState !== 'default' && usageState !== '50%' && (
          <div className={creditUsageBreakdownVariants({ usageState }).banner()}>
            <CautionIcon className={creditUsageBreakdownVariants({ usageState }).bannerIcon()} />
            <span className={creditUsageBreakdownVariants({ usageState }).bannerText()}>
              {usageState === 'depleted'
                ? "You've depleted your credits"
                : `You've used ${percentageUsed}% of your plan`}
            </span>
          </div>
        )}
        <div className="p-4">
          <div className="flex flex-col gap-2">
            <p className="font-semibold text-10 text-gray-12 uppercase tracking-wider">Workspace Credit Usage</p>
            {includeMeterBar && <CreditUsageMeterBar {...creditUsageStats} />}
            {CREDIT_USAGE_CATEGORIES.map((category) => {
              const usage = used[category];

              if (!subscription?.isCreditModel && !usage.credits && category !== 'generative') {
                return null;
              }

              return <CreditUsageBreakdownCategory key={category} category={category} {...usage} />;
            })}
          </div>

          <div className="flex items-center gap-2 pt-4 empty:hidden">
            {showPlansButton && (
              <Button
                type="button"
                color="blue"
                appearance="filled"
                size="small"
                className="w-full flex-1"
                onClick={handleSeePlansClick}
              >
                Upgrade
              </Button>
            )}
          </div>
        </div>
        <div className="border-gray-4 border-t px-3 py-2">
          <CreditUsageMeterResetDate nextCycleStartAt={nextCycleStartAt} />
        </div>
      </div>
    );
  },
);

CreditUsageBreakdown.displayName = 'CreditUsageBreakdown';
